import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { paramsSerializer } from '../axios';

import { addAuthorizationHeader, refreshTokens, uefatvClientEvents } from './domain';
import { getNewToken } from './domain.new';
import { clearStoredTokens } from './repository';

import EventBusInstance from '@core/eventbus';
import { Warn } from '@utilities/logging';
import { uefaTvApiKey, uefaTvRealm } from '@utilities/WindowContext';

let errorCounter = 0; /**
 * Creates an Axios client instance with the necessary configuration for the UEFA TV API.
 * The client includes interceptors to handle token refreshing and other error cases.
 * Look here for more details https://dicetech.atlassian.net/wiki/spaces/DD/pages/524409/User
 * @param baseURL - The base URL for the UEFA TV API.
 * @returns An Axios client instance configured for the UEFA TV API.
 */
const uefaTvClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': uefaTvApiKey,
    Realm: uefaTvRealm,
    app: 'vesper',
  },
  paramsSerializer,
});

uefaTvClient.interceptors.request.use(
  async (config) => {
    return await addAuthorizationHeader(config);
  },
  (error) => Promise.reject(error),
);
uefaTvClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    return await handleUefaTvClientError(error, uefaTvClient);
  },
);

export async function handleUefaTvClientError(error: AxiosError, client: typeof uefaTvClient) {
  const originalRequest = error.config;
  switch (error.response?.status) {
    case 401:
      try {
        const refreshData = await refreshTokens();
        originalRequest.headers['Authorization'] = `Bearer ${refreshData.authorisationToken}`;

        return client(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    case 403:
    case 404:
      try {
        const response = await getNewToken();
        originalRequest.headers['Authorization'] = `Bearer ${response.authorisationToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    default:
      return Promise.reject(error);
  }
}
export const apiGet = async <TResponse>(
  url: string,
  baseURL: string,
  params: Record<string, any>,
  signal: AbortSignal,
  config?: AxiosRequestConfig,
) => {
  try {
    const response = await uefaTvClient.get<TResponse>(url, { params, baseURL, signal, ...config });
    return response.data;
  } catch (error) {
    errorCounter++;
    if (errorCounter > 5) clearStoredTokens();
    if (errorCounter > 10) {
      //workaround for infinite loop erros
      EventBusInstance.dispatch(uefatvClientEvents.ERROR, { message: 'Too many errors', status: 422 });
    } else {
      EventBusInstance.dispatch(uefatvClientEvents.ERROR, { ...error });
    }
    Warn(error);
    throw error;
  }
};
